import React, { useState } from 'react';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

const MotionBox = motion(Box);
const MotionTypography = motion(Typography);

const PartnersCarousel = () => {
  const { t } = useTranslation();
  const theme = useTheme(); 
  const [isHovering, setIsHovering] = useState(false);

  // Detect if the app is currently in Right-to-Left mode
  const isRtl = theme.direction === 'rtl';

  // Partner logos
  const partners = [
    { id: 1, name: 'Partner', path: 'partnerLogos/Partner_logo.svg' },
    { id: 2, name: 'Sport1', path: 'partnerLogos/sport1_logo.svg' },
    { id: 3, name: 'Walla', path: 'partnerLogos/Walla2024.svg' },
    { id: 4, name: 'Mako Keshet', path: 'partnerLogos/Mako_keshet_2024.svg' },
    { id: 5, name: '012 Mobile', path: 'partnerLogos/012_Mobile_Logo.svg' },
    { id: 6, name: 'Eco 99FM', path: 'partnerLogos/Eco_99_fm.svg' },
    { id: 7, name: 'Celcom', path: 'partnerLogos/celcom_2022.svg' },
    { id: 8, name: 'Toto', path: 'partnerLogos/toto_logo.svg' },
  ];

  const trackLogos = [...partners, ...partners];

  // --- TWEAK 1: DIRECTION ---
  // Swapped the values to reverse direction.
  // Standard LTR usually wants to move Left (0 to -100).
  // If you want it to move Right, use: ['-100%', '0%'].
  const xAnimation = isRtl 
    ? ['100%', '0%']   // RTL: Moves Left (reversed)
    : ['-100%', '0%']; // LTR: Moves Right (reversed)

  return (
    <Box
      component="section"
      sx={{
        py: { xs: 6, md: 8 },
        background: 'linear-gradient(180deg, var(--bg-primary) 0%, var(--bg-secondary) 100%)',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <MotionTypography
            component="h2"
            variant="h3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            sx={{
              fontSize: { xs: '1.8rem', md: '2.2rem' },
              fontWeight: 700,
              color: 'var(--text-primary)',
              mb: 2,
            }}
          >
            {t('partners.title')}
          </MotionTypography>
          <MotionBox
            initial={{ opacity: 0, scaleX: 0 }}
            whileInView={{ opacity: 1, scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2, ease: 'easeOut' }}
            sx={{
              height: '3px',
              width: '60px',
              background: 'var(--gradient-primary)',
              borderRadius: 'var(--radius-full)',
              mx: 'auto',
              mb: 2,
            }}
          />
        </Box>

        {/* Carousel Wrapper */}
        <Box
          onMouseEnter={() => setIsHovering(true)}
          onMouseLeave={() => setIsHovering(false)}
          sx={{
            display: 'flex',
            overflow: 'hidden',
            width: '100%',
            maskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
            WebkitMaskImage: 'linear-gradient(to right, transparent, black 10%, black 90%, transparent)',
          }}
        >
          {/* Loop for Double Track */}
          {[...Array(2)].map((_, trackIndex) => (
            <MotionBox
              key={`track-${trackIndex}`}
              animate={{ x: xAnimation }}
              transition={{
                repeat: Infinity,
                repeatType: "loop",
                // --- TWEAK 2: SPEED ---
                // Changed from 35 to 80. Higher = Slower.
                duration: 80, 
                ease: "linear",
              }}
              style={{
                display: 'flex',
                animationPlayState: isHovering ? 'paused' : 'running',
              }}
              sx={{
                flexShrink: 0,
                gap: { xs: 4, sm: 6, md: 8 },
                pr: isRtl ? 0 : { xs: 4, sm: 6, md: 8 },
                pl: isRtl ? { xs: 4, sm: 6, md: 8 } : 0,
                alignItems: 'center',
                willChange: 'transform',
                transform: 'translateZ(0)',
              }}
            >
              {trackLogos.map((partner, index) => (
                <Box
                  key={`logo-${index}`}
                  sx={{
                    flex: '0 0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minWidth: '120px',
                  }}
                >
                  <Box
                    component="img"
                    src={`/${partner.path}`}
                    alt={partner.name}
                    sx={{
                      height: '60px',
                      width: 'auto',
                      maxWidth: '160px',
                      objectFit: 'contain',
                      filter: 'grayscale(100%)',
                      opacity: 0.6,
                      transition: 'all 0.3s ease',
                      cursor: 'pointer',
                      '&:hover': { filter: 'grayscale(0%)', opacity: 1, transform: 'scale(1.1)' },
                      // Most partner logos are black; invert them so they stay
                      // visible on the dark background.
                      '[data-theme="dark"] &': { filter: 'grayscale(100%) invert(1)' },
                      '[data-theme="dark"] &:hover': { filter: 'grayscale(100%) invert(1)', opacity: 1, transform: 'scale(1.1)' },
                    }}
                  />
                </Box>
              ))}
            </MotionBox>
          ))}
        </Box>
      </Container>
    </Box>
  );
};

export default PartnersCarousel;